import * as discountCodeHelper from '../../helpers/discount_codes';
import {Badge, InlineStack} from "@shopify/polaris";

export function calculateTotalPrice (lineItem) {
    const { price } = lineItem;
    let basePrice = (price.manual === null)
        ? price.discounted
        : (price.manual || 0); // the manual price might be an empty string

    let total = (lineItem.quantity * basePrice);

    if (lineItem.price.discount && lineItem.price.discount.quantity > 0) {
        total -= lineItem.price.discount.total;
    }

    return total;
}

export function formatLocationAddress (locationAddress, customer, phone) {
    return locationAddress
        ? {
            firstName: locationAddress.first_name ?? customer.additional_data.first_name,
            lastName: locationAddress.last_name ?? customer.additional_data.last_name,
            company: locationAddress.company,
            address1: locationAddress.address1,
            address2: locationAddress.address2,
            city: locationAddress.city,
            province: locationAddress.state,
            country: locationAddress.country,
            zip: locationAddress.zip_code,
            phone: phone
        }
        : null;
}

export function getDiscountDetails (lineItem) {
    const { price } = lineItem;

    // we have to round it, otherwise JS will do some weird stuff with it
    const totalDiscount = parseFloat(parseFloat(price.discount?.total ?? 0).toFixed(2));

    if (totalDiscount == 0) {
        const manual = parseFloat(price.manual);
        const discounted = parseFloat(price.discounted);
        const original = parseFloat(price.original);

        if (price.manual && manual > 0) {
            return {
                valueType: 'FIXED_AMOUNT',
                value: Number((original - manual).toFixed(2)),
            }
        }

        if (original > discounted) {
            return {
                valueType: 'FIXED_AMOUNT',
                value: Number((original - discounted).toFixed(2)),
            }
        }

        return null;
    }

    return {
        valueType: 'FIXED_AMOUNT',
        value: Number((totalDiscount / lineItem.quantity).toFixed(2)),
    };
}

export function applyDiscountCode (lineItems, buckets) {
    const originalSortOrder = lineItems.map(item => item.id);
    let updatedLineItems = [...lineItems]
        .sort(
            (left, right) => parseFloat(left.price.discounted) - parseFloat(right.price.discounted)
        )
        .map(item => {
            item.price.discount = null;

            return item;
        });

    buckets.forEach((productBucket) => {
        updatedLineItems = updatedLineItems.map(item => {
            const quantity = parseInt(item.quantity, 10);
            let totalPrice = parseFloat(item.quantity * item.price.original);
            let itemDiscount = item.price.discount ?? {
                quantity: 0,
                total: 0,
                discountCodes: [],
            };
            let unitPrice = (totalPrice - itemDiscount.total) / quantity;

            let ids = productBucket.origin_products ?? [productBucket.id];

            if (
                item.price.manual
                || !productBucket.eligible
                || ! quantity
                || !ids.includes(item.shopify_product_id)
            ) {
                return item;
            }

            if (productBucket.eligible_amount === -1) {
                let newTotalPrice = parseFloat(discountCodeHelper.calculateTotalPrice(productBucket.discountCode, unitPrice, quantity)
                    .toFixed(2));

                itemDiscount.quantity = quantity;
                itemDiscount.total += newTotalPrice;
                itemDiscount.discountCodes.push(productBucket.discountCode.code);

                item.price.discount = itemDiscount;


                return item;
            }

            const eligibleAmount = Math.min(productBucket.eligible_amount, quantity);

            if (eligibleAmount > itemDiscount.quantity)
                itemDiscount.quantity = eligibleAmount;

            let newTotalPrice = parseFloat(discountCodeHelper.calculateTotalPrice(productBucket.discountCode, unitPrice, eligibleAmount)
                .toFixed(2));
            itemDiscount.total += newTotalPrice;
            itemDiscount.discountCodes.push(productBucket.discountCode.code);
            item.price.discount = itemDiscount;

            productBucket.eligible_amount -= eligibleAmount;

            return item;
        });
    });

    return {
        lineItems: updatedLineItems
            .map(item => {
                //We do that to ensure that we will not have rounding issues.
                if (item.price.discount && item.price.discount.quantity > 0) {
                    let unitDiscount = parseFloat((item.price.discount.total / item.quantity).toFixed(2));

                    item.price.discount.total = unitDiscount * item.quantity;

                    if (item.price.discount.total > item.price.total) {
                        item.price.discount.total = item.price.total;
                    }
                } else {
                    item.price.discount = null;
                }
                return item;
            })
            .sort(
                (left, right) => originalSortOrder.indexOf(left.id) - originalSortOrder.indexOf(right.id)
            ),
    }
}

export function getOrderBadges(order) {
    const badges = [];

    const isMerged = !!order?.merged_into_order || !!order?.merging;
    const isCombined = order?.merged_orders?.length && !order?.is_reorder;
    const isReorder = order?.merged_orders?.length && order?.is_reorder;
    const hasPackDate = !isMerged && order?.pack_date;
    const isSubmitted = !isMerged && order?.submitted;
    const isSwitch = order?.has_switch_mode_tag;

    const isNew = !hasPackDate && !isSubmitted && !isMerged && !isCombined && !isReorder && !isSwitch;

    if (isNew) {
        badges.push(<Badge tone="success">NEW</Badge>);
    }

    if (hasPackDate) {
        badges.push(<Badge>Pack Date Set</Badge>);
    }

    if (isSubmitted) {
        badges.push(<Badge tone="info">SUBMITTED</Badge>);
    }

    if (isMerged) {
        badges.push(<Badge tone="warning" progress={order?.merging ? 'partiallyComplete' : 'complete'}>{order?.merging ? 'MERGING' : 'MERGED'}</Badge>);
    }

    if (!isMerged && isCombined) {
        badges.push(<Badge tone="warning" progress={order?.merging ? 'partiallyComplete' : 'complete'}>{order?.merging ? 'COMBINING' : 'COMBINED'}</Badge>);
    }

    if (!isMerged && isReorder) {
        badges.push(<Badge tone="critical" progress={order?.merging ? 'partiallyComplete' : 'complete'}>{order?.merging ? 'REORDERING' : 'REORDER'}</Badge>);
    }

    if (isSwitch) {
        badges.push(<Badge tone="attention">Switch Mode</Badge>);
    }

    return <InlineStack gap={200}>{badges}</InlineStack>;
};

// Rendered in its own dedicated (header-less) column, separate from getOrderBadges,
// so it never affects an order's existing tags.
export function getFulfillmentBadge(order) {
    if (order?.order_status !== 'fulfilled') {
        return null;
    }

    return <Badge tone="success">FULFILLED</Badge>;
};
